import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

function useIntersection(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

type SubmitStatus = "idle" | "loading" | "success" | "error";

export default function ContactSection() {
  const { t } = useTranslation();
  const { ref, visible } = useIntersection();
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const message = (data.get("message") as string) || "";
    if (message.length > 500) return;

    setStatus("loading");
    try {
      const params = new URLSearchParams();
      data.forEach((val, key) => params.append(key, val as string));
      const res = await fetch("https://readdy.ai/api/form/d8gm958b91vaa813doqg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const contactInfoItems = [
    { icon: "ri-global-line", labelKey: "contact.website_label", value: "ikob.kr", href: "https://ikob.kr" },
    { icon: "ri-mail-line", labelKey: "contact.email_info_label", value: "ikob@ikob.kr", href: "mailto:ikob@ikob.kr" },
    { icon: "ri-phone-line", labelKey: "contact.phone_label", value: "02-6478-0021", href: "tel:+82264780021" },
    { icon: "ri-map-pin-2-line", labelKey: "contact.address_label", valueKey: "contact.address_val", href: null },
    { icon: "ri-smartphone-line", labelKey: "contact.momo_label", value: "MOMO", href: "#", isMomoLink: true },
  ];

  const inquiryTypes = [
    t("contact.type1"),
    t("contact.type2"),
    t("contact.type3"),
    t("contact.type4"),
    t("contact.type5"),
  ];

  return (
    <section
      id="contact"
      className="py-24 md:py-32 px-4"
      style={{ backgroundColor: "oklch(0.10 0.001 10)" }}
    >
      <div className="max-w-7xl mx-auto">
        <div
          ref={ref}
          className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          {/* Left: Info */}
          <div>
            <p className="text-xs font-label tracking-widest uppercase mb-4" style={{ color: "oklch(0.74 0.18 55)" }}>
              {t("contact.label")}
            </p>
            <h2 className="font-heading font-black text-3xl md:text-5xl leading-tight mb-6" style={{ color: "white" }}>
              {t("contact.title1")}<br />
              {t("contact.title2")}<br />
              {t("contact.title3")}
            </h2>
            <p
              className="font-label text-sm md:text-base leading-relaxed mb-10 whitespace-pre-line"
              style={{ color: "rgba(255,255,255,0.45)" }}
            >
              {t("contact.desc")}
            </p>

            <div className="space-y-5">
              {contactInfoItems.map((item) => (
                <div key={item.labelKey} className="flex gap-4 items-start">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: "rgba(245,158,11,0.10)" }}
                  >
                    <i className={`${item.icon} text-base`} style={{ color: "oklch(0.74 0.18 55)" }}></i>
                  </div>
                  <div>
                    <p className="text-xs font-label mb-0.5" style={{ color: "rgba(255,255,255,0.35)" }}>
                      {t(item.labelKey)}
                    </p>
                    {item.href ? (
                      <a
                        href={item.isMomoLink ? "#momo" : item.href}
                        target={!item.isMomoLink && item.href.startsWith("http") ? "_blank" : undefined}
                        rel={!item.isMomoLink && item.href.startsWith("http") ? "noopener noreferrer nofollow" : undefined}
                        onClick={item.isMomoLink ? (e) => {
                          e.preventDefault();
                          const el = document.getElementById("momo");
                          if (el) el.scrollIntoView({ behavior: "smooth" });
                        } : undefined}
                        className="text-sm font-label hover:underline whitespace-pre-line"
                        style={{ color: "rgba(255,255,255,0.75)" }}
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-sm font-label whitespace-pre-line" style={{ color: "rgba(255,255,255,0.75)" }}>
                        {item.valueKey ? t(item.valueKey) : item.value}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Form */}
          <div
            className="rounded-2xl p-6 md:p-8"
            style={{ backgroundColor: "oklch(0.15 0.001 10)", border: "1px solid rgba(255,255,255,0.06)" }}
          >
            {status === "success" ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                  style={{ backgroundColor: "oklch(0.74 0.18 55)" }}
                >
                  <i className="ri-check-line text-3xl" style={{ color: "oklch(0.10 0.001 10)" }}></i>
                </div>
                <h3 className="font-heading font-bold text-xl mb-2" style={{ color: "white" }}>
                  {t("contact.success_title")}
                </h3>
                <p className="text-sm font-label" style={{ color: "rgba(255,255,255,0.45)" }}>
                  {t("contact.success_desc")}
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="cursor-pointer mt-6 px-6 py-2.5 rounded-full text-sm font-label font-medium border transition-colors whitespace-nowrap"
                  style={{ color: "rgba(255,255,255,0.6)", borderColor: "rgba(255,255,255,0.15)" }}
                >
                  {t("contact.retry")}
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="ikob-contact-form"
                onSubmit={handleSubmit}
                className="flex flex-col gap-4"
              >
                <h3 className="font-heading font-bold text-lg mb-1" style={{ color: "white" }}>
                  {t("contact.form_title")}
                </h3>

                <div>
                  <label className="block text-xs font-label mb-1.5" style={{ color: "rgba(255,255,255,0.45)" }}>
                    {t("contact.name_label")}
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder={t("contact.name_placeholder")}
                    className="w-full px-4 py-3 rounded-xl text-sm font-label outline-none transition-colors bg-transparent"
                    style={{ color: "rgba(255,255,255,0.85)", border: "1px solid rgba(255,255,255,0.10)" }}
                  />
                </div>

                <div>
                  <label className="block text-xs font-label mb-1.5" style={{ color: "rgba(255,255,255,0.45)" }}>
                    {t("contact.email_label")}
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder={t("contact.email_placeholder")}
                    className="w-full px-4 py-3 rounded-xl text-sm font-label outline-none transition-colors bg-transparent"
                    style={{ color: "rgba(255,255,255,0.85)", border: "1px solid rgba(255,255,255,0.10)" }}
                  />
                </div>

                <div>
                  <label className="block text-xs font-label mb-1.5" style={{ color: "rgba(255,255,255,0.45)" }}>
                    {t("contact.type_label")}
                  </label>
                  <select
                    name="inquiry_type"
                    className="w-full px-4 py-3 rounded-xl text-sm font-label outline-none transition-colors cursor-pointer"
                    style={{
                      backgroundColor: "oklch(0.15 0.001 10)",
                      color: "rgba(255,255,255,0.75)",
                      border: "1px solid rgba(255,255,255,0.10)",
                    }}
                  >
                    {inquiryTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-label mb-1.5" style={{ color: "rgba(255,255,255,0.45)" }}>
                    {t("contact.msg_label")}
                    <span className={`ml-2 ${charCount > 480 ? "text-red-400" : "opacity-50"}`}>
                      ({charCount}/500)
                    </span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder={t("contact.msg_placeholder")}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 rounded-xl text-sm font-label outline-none transition-colors bg-transparent resize-none"
                    style={{ color: "rgba(255,255,255,0.85)", border: "1px solid rgba(255,255,255,0.10)" }}
                  ></textarea>
                </div>

                {status === "error" && (
                  <p className="text-red-400 text-xs font-label">{t("contact.error")}</p>
                )}

                <button
                  type="submit"
                  disabled={status === "loading" || charCount > 500}
                  className="cursor-pointer whitespace-nowrap w-full py-3.5 rounded-full font-label font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ backgroundColor: "oklch(0.74 0.18 55)", color: "oklch(0.10 0.001 10)" }}
                >
                  {status === "loading" ? (
                    <><i className="ri-loader-4-line animate-spin"></i>{t("contact.sending")}</>
                  ) : (
                    <><i className="ri-send-plane-line"></i>{t("contact.submit")}</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}